import React, { useEffect, useRef, useState } from "react"
import { Badge, Button, Popover, Radio, Select, Tag } from "antd"
import { MobileOutlined, PhoneOutlined } from "@ant-design/icons"
import { Icon } from "components"
import Strings from "utils/strings"
import { OrderFilters } from "../utils"

// Filter values shared with the API (dingoo-api getOrders)
export type BagFilter = "with_bag" | "box_only" | undefined
// "with_phone" is the legacy alias of "any_phone" and is still accepted by the API.
export type PhoneFilter =
	| "without_phone"
	| "any_phone"
	| "home_phone"
	| "mobile_phone"
	| undefined
export type PaymentFilter = "with_payment" | "without_payment" | undefined
// "only_bike" is the backoffice override. For van: "any_van" is any order
// that ended up going by van (orderFlow.vanStop) for any reason; "only_van"
// narrows to the backoffice override, "promoted_van" to the algorithm's own
// decision (vanStop true without an onlyVan override).
export type DeliveryModeFilter =
	| "only_bike"
	| "any_van"
	| "only_van"
	| "promoted_van"
	| undefined

export type GeoZone = {
	_id: string
	name: string
	geojson?: any
	// True for zones the screen derives itself (the Uber limits circle) rather
	// than GeojsonZones documents.
	automatic?: boolean
}

// Equal-width segmented buttons: every button matches the widest one, text on
// a single line (no wrap/clip).
const segmentedGroup: React.CSSProperties = {
	display: "grid",
	gridTemplateColumns: "repeat(3, 1fr)",
	width: "100%",
}
// Sub-row refining a presence choice above (phone type, or van kind). It only
// appears once the parent option ("with phone", "van") is chosen, which is
// what marks it as a refinement of the row above.
const subRowGroup: React.CSSProperties = {
	...segmentedGroup,
	marginTop: 6,
}
// Icon and label are centred together as one unit. Left to itself the icon
// sits on the text baseline, which reads a couple of pixels low next to a
// smaller glyph.
const phoneTypeOption: React.CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	gap: 4,
}
const phoneTypeIcon: React.CSSProperties = {
	fontSize: 12,
}
const fullWidthRadioButton: React.CSSProperties = {
	textAlign: "center",
	whiteSpace: "nowrap",
}
// Marker shown next to zones the screen derives itself (see GeoZone.automatic).
const automaticZoneTag: React.CSSProperties = {
	marginLeft: 8,
	fontSize: 11,
	lineHeight: "16px",
}

interface OrderAttributeFilterDropdownProps {
	// All attribute filters travel together in one object (minimizes props).
	filters: OrderFilters
	setFilters: (value: OrderFilters) => void
	// Zones for the currently selected contract (empty until a contract is chosen).
	geoZones: GeoZone[]
	contractSelected: boolean
}

// Only these keys reach the API, so they decide whether a fetch is warranted.
const FILTER_KEYS: (keyof OrderFilters)[] = [
	"bag",
	"phone",
	"payment",
	"deliveryMode",
	"geoZoneId",
]

const areFiltersEqual = (a: OrderFilters, b: OrderFilters) =>
	FILTER_KEYS.every((key) => (a[key] ?? null) === (b[key] ?? null))

const OrderAttributeFilterDropdown: React.FC<
	OrderAttributeFilterDropdownProps
> = ({ filters, setFilters, geoZones, contractSelected }) => {
	// Selections are held locally while the popover is open so several filters
	// can be set in one go; they are committed (and the orders refetched) only
	// once it closes.
	const [draftFilters, setDraftFilters] = useState<OrderFilters>(filters)

	// The committed filters when the popover opened, to compare against on close.
	const filtersOnOpenRef = useRef<OrderFilters>(filters)

	// Follow the committed filters when they change outside the popover — the
	// orders screen clears the geo zone whenever the contract changes.
	useEffect(() => {
		setDraftFilters(filters)
	}, [filters])

	const handleVisibleChange = (visible: boolean) => {
		if (visible) {
			filtersOnOpenRef.current = draftFilters
		} else if (!areFiltersEqual(filtersOnOpenRef.current, draftFilters)) {
			setFilters(draftFilters)
		}
	}

	const bagFilter = draftFilters.bag as BagFilter
	// "with_phone" is the legacy value for "any kind of phone" — normalize it
	// here so the rest of the component only deals with the current values.
	const phoneFilter = (
		draftFilters.phone === "with_phone" ? "any_phone" : draftFilters.phone
	) as PhoneFilter
	const paymentFilter = draftFilters.payment as PaymentFilter
	const deliveryModeFilter = draftFilters.deliveryMode as DeliveryModeFilter
	const geoZoneId = draftFilters.geoZoneId || undefined

	const activeCount =
		(bagFilter ? 1 : 0) +
		(phoneFilter ? 1 : 0) +
		(paymentFilter ? 1 : 0) +
		(deliveryModeFilter ? 1 : 0) +
		(geoZoneId ? 1 : 0)
	const isActive = activeCount > 0

	const patch = (partial: Partial<OrderFilters>) =>
		setDraftFilters({ ...draftFilters, ...partial })

	// The phone filter is one value that answers two questions: does the order
	// have a phone at all, and — only then — which kind of number is it. Split it
	// so each question gets its own control.
	const phonePresence =
		phoneFilter === "without_phone"
			? "without_phone"
			: phoneFilter
				? "with_phone"
				: "all"

	const setPhonePresence = (presence: string) =>
		patch({
			phone:
				presence === "all"
					? undefined
					: presence === "without_phone"
						? "without_phone"
						: // Narrowing to home/mobile is the sub-row's job; start unfiltered.
							"any_phone",
		})

	// Same split as phone: does it go by van at all, and — only then — which
	// kind of van (any / forced / promoted).
	const deliveryModePresence =
		deliveryModeFilter === "only_bike"
			? "only_bike"
			: deliveryModeFilter
				? "van"
				: "all"

	const setDeliveryModePresence = (presence: string) =>
		patch({
			deliveryMode:
				presence === "all"
					? undefined
					: presence === "only_bike"
						? "only_bike"
						: // Narrowing to forced/promoted is the sub-row's job; start unfiltered.
							"any_van",
		})

	const content = (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: 16,
				// 380 rather than 340: at the compact size the longest Portuguese
				// labels ("Sem pagamento") no longer overflow their segment.
				minWidth: 380,
			}}
		>
			<div>
				<div style={{ fontWeight: 500, marginBottom: 8 }}>
					{Strings.orders.packageLabel}
				</div>
				<Radio.Group
					size="small"
					style={segmentedGroup}
					value={bagFilter ?? "all"}
					onChange={(e) => {
						const value = e.target.value
						patch({ bag: value === "all" ? undefined : (value as BagFilter) })
					}}
				>
					<Radio.Button value="all" style={fullWidthRadioButton}>
						{Strings.generic.all}
					</Radio.Button>
					<Radio.Button value="with_bag" style={fullWidthRadioButton}>
						{Strings.orders.withBag}
					</Radio.Button>
					<Radio.Button value="box_only" style={fullWidthRadioButton}>
						{Strings.orders.boxOnly}
					</Radio.Button>
				</Radio.Group>
			</div>

			<div>
				<div style={{ fontWeight: 500, marginBottom: 8 }}>
					{Strings.orders.phoneLabel}
				</div>
				<Radio.Group
					size="small"
					style={segmentedGroup}
					value={phonePresence}
					onChange={(e) => setPhonePresence(e.target.value)}
				>
					<Radio.Button value="all" style={fullWidthRadioButton}>
						{Strings.generic.all}
					</Radio.Button>
					<Radio.Button value="without_phone" style={fullWidthRadioButton}>
						{Strings.orders.noPhone}
					</Radio.Button>
					<Radio.Button value="with_phone" style={fullWidthRadioButton}>
						{Strings.orders.withPhone}
					</Radio.Button>
				</Radio.Group>

				{/* Which kind of number only matters once "with phone" is chosen, so
				    it stays out of the way until then. */}
				{phonePresence === "with_phone" && (
					<Radio.Group
						size="small"
						style={subRowGroup}
						value={phoneFilter}
						onChange={(e) => patch({ phone: e.target.value as PhoneFilter })}
					>
						<Radio.Button value="any_phone" style={fullWidthRadioButton}>
							{Strings.orders.anyPhone}
						</Radio.Button>
						<Radio.Button value="home_phone" style={fullWidthRadioButton}>
							<span style={phoneTypeOption}>
								<PhoneOutlined style={phoneTypeIcon} />
								{Strings.orders.homePhone}
							</span>
						</Radio.Button>
						<Radio.Button value="mobile_phone" style={fullWidthRadioButton}>
							<span style={phoneTypeOption}>
								<MobileOutlined style={phoneTypeIcon} />
								{Strings.orders.mobilePhone}
							</span>
						</Radio.Button>
					</Radio.Group>
				)}
			</div>

			<div>
				<div style={{ fontWeight: 500, marginBottom: 8 }}>
					{Strings.orders.paymentLabel}
				</div>
				<Radio.Group
					size="small"
					style={segmentedGroup}
					value={paymentFilter ?? "all"}
					onChange={(e) => {
						const value = e.target.value
						patch({
							payment: value === "all" ? undefined : (value as PaymentFilter),
						})
					}}
				>
					<Radio.Button value="all" style={fullWidthRadioButton}>
						{Strings.generic.all}
					</Radio.Button>
					<Radio.Button value="with_payment" style={fullWidthRadioButton}>
						{Strings.orders.withPayment}
					</Radio.Button>
					<Radio.Button value="without_payment" style={fullWidthRadioButton}>
						{Strings.orders.withoutPayment}
					</Radio.Button>
				</Radio.Group>
			</div>

			<div>
				<div style={{ fontWeight: 500, marginBottom: 8 }}>
					{Strings.orders.deliveryModeLabel}
				</div>
				<Radio.Group
					size="small"
					style={segmentedGroup}
					value={deliveryModePresence}
					onChange={(e) => setDeliveryModePresence(e.target.value)}
				>
					<Radio.Button value="all" style={fullWidthRadioButton}>
						{Strings.generic.all}
					</Radio.Button>
					<Radio.Button value="only_bike" style={fullWidthRadioButton}>
						{Strings.orders.onlyBikeOption}
					</Radio.Button>
					<Radio.Button value="van" style={fullWidthRadioButton}>
						{Strings.orders.vanOption}
					</Radio.Button>
				</Radio.Group>

				{/* Which kind of van only matters once "van" is chosen, so it stays
				    out of the way until then. */}
				{deliveryModePresence === "van" && (
					<Radio.Group
						size="small"
						style={subRowGroup}
						value={deliveryModeFilter}
						onChange={(e) =>
							patch({ deliveryMode: e.target.value as DeliveryModeFilter })
						}
					>
						<Radio.Button value="any_van" style={fullWidthRadioButton}>
							{Strings.orders.anyVanOption}
						</Radio.Button>
						<Radio.Button value="only_van" style={fullWidthRadioButton}>
							{Strings.orders.forcedVanOption}
						</Radio.Button>
						<Radio.Button value="promoted_van" style={fullWidthRadioButton}>
							{Strings.orders.promotedVanOption}
						</Radio.Button>
					</Radio.Group>
				)}
			</div>

			<div>
				<div style={{ fontWeight: 500, marginBottom: 8 }}>
					{Strings.orders.geoZoneLabel}
				</div>
				<Select
					style={{ width: "100%" }}
					placeholder={Strings.orders.geoZonePlaceholder}
					value={geoZoneId}
					onChange={(value: string) => patch({ geoZoneId: value || undefined })}
					allowClear
					showSearch
					optionFilterProp="children"
					disabled={!contractSelected}
				>
					{geoZones.map((zone) => (
						<Select.Option key={zone._id} value={zone._id}>
							{zone.name}
							{/* null rather than false: Select stringifies children when
							    filtering, and false would leak into the search text. */}
							{zone.automatic ? (
								<Tag color="blue" style={automaticZoneTag}>
									{Strings.orders.geoZoneAutomatic}
								</Tag>
							) : null}
						</Select.Option>
					))}
				</Select>
				{!contractSelected && (
					<div style={{ color: "#999", fontSize: 12, marginTop: 4 }}>
						{Strings.orders.geoZoneSelectContract}
					</div>
				)}
			</div>
		</div>
	)

	return (
		<Popover
			content={content}
			title={Strings.orders.filterByAttributes}
			trigger="click"
			placement="bottomRight"
			onVisibleChange={handleVisibleChange}
		>
			<Badge count={activeCount} size="small" offset={[-2, 2]}>
				<Button
					style={{
						display: "flex",
						alignItems: "center",
						gap: 6,
						backgroundColor: isActive ? "#e6f7ff" : undefined,
						borderColor: isActive ? "#1890ff" : undefined,
					}}
				>
					<Icon name="filter" style={{ fontSize: 14 }} />
				</Button>
			</Badge>
		</Popover>
	)
}

export default OrderAttributeFilterDropdown
